import React, { createContext, useContext, useCallback, useReducer, useEffect, useRef } from 'react';
import { listMeeting, getMeeting, delete_meeting, like, searchMeetings } from '../components/Network';
import { idKey, pinedKey, titleKey, durationKey, isSummarizedKey, dateKey, summaryKey } from '../components/MeetingsKeysList';

// Action types for the reducer
const ACTIONS = {
  SET_MEETINGS: 'SET_MEETINGS',
  SET_LOADING: 'SET_LOADING',
  SET_SORTING_LOADING: 'SET_SORTING_LOADING',
  SET_ERROR: 'SET_ERROR',
  UPDATE_MEETING: 'UPDATE_MEETING',
  DELETE_MEETING: 'DELETE_MEETING',
  SET_CURRENT_SELECTED_MEETING: 'SET_CURRENT_SELECTED_MEETING',
  CLEAR_CURRENT_SELECTED_MEETING: 'CLEAR_CURRENT_SELECTED_MEETING',
  SET_SEARCH_RESULTS: 'SET_SEARCH_RESULTS',
  SET_SEARCHING: 'SET_SEARCHING',
  CLEAR_SEARCH: 'CLEAR_SEARCH',
  SET_SORT_PARAMS: 'SET_SORT_PARAMS',
  UPDATE_MEETINGS_AND_SELECTED: 'UPDATE_MEETINGS_AND_SELECTED'
};

// Initial state
const initialState = {
  meetings: [], // List meetings (title + summary only)
  currentSelectedMeeting: null, // Currently selected meeting with full data
  loading: true,
  sortingLoading: false,
  error: null,
  searchResults: null,
  isSearching: false,
  // Sorting state
  sortBy: 'id',
  sortDescending: true
};

// Helper function to deeply compare meetings arrays
const areMeetingsEqual = (prevMeetings, nextMeetings) => {
  if (prevMeetings.length !== nextMeetings.length) {
    return false;
  }
  
  for (let i = 0; i < prevMeetings.length; i++) {
    const prev = prevMeetings[i];
    const next = nextMeetings[i];
    
    // Compare ALL essential properties that could affect rendering
    if (
      prev[idKey] !== next[idKey] ||
      prev[titleKey] !== next[titleKey] ||
      prev[isSummarizedKey] !== next[isSummarizedKey] ||
      prev[pinedKey] !== next[pinedKey] ||
      prev[dateKey] !== next[dateKey] ||
      prev[summaryKey] !== next[summaryKey] ||
      prev[durationKey] !== next[durationKey]
    ) {
      return false;
    }
  }
  
  return true;
};

// Reducer for managing meeting state
const meetingReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_MEETINGS:
          // Check if meetings have actually changed to avoid unnecessary re-renders
      if (areMeetingsEqual(state.meetings, action.payload)) {
        return state; // Return same state to prevent re-render
      }
      return { ...state, meetings: action.payload };
    
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case ACTIONS.SET_SORTING_LOADING:
      return { ...state, sortingLoading: action.payload };
    
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload };
    
    case ACTIONS.UPDATE_MEETING:
      const { meetingId, updates } = action.payload;
      
      // Update currently selected meeting if it matches
      const updatedCurrentMeeting = state.currentSelectedMeeting && 
        state.currentSelectedMeeting[idKey] === meetingId 
        ? { ...state.currentSelectedMeeting, ...updates }
        : state.currentSelectedMeeting;
      
      return {
        ...state,
        meetings: state.meetings.map(meeting => 
          meeting[idKey] === meetingId ? { ...meeting, ...updates } : meeting
        ),
        currentSelectedMeeting: updatedCurrentMeeting
      };
    
    case ACTIONS.DELETE_MEETING:
      const deletedId = action.payload;
      
      // Clear current selected meeting if it's the one being deleted
      const clearedCurrentMeeting = state.currentSelectedMeeting && 
        state.currentSelectedMeeting[idKey] === deletedId 
        ? null 
        : state.currentSelectedMeeting;
      
      return {
        ...state,
        meetings: state.meetings.filter(meeting => meeting[idKey] !== deletedId),
        currentSelectedMeeting: clearedCurrentMeeting
      };
    
    case ACTIONS.SET_CURRENT_SELECTED_MEETING:
      return {
        ...state,
        currentSelectedMeeting: action.payload
      };
    
    case ACTIONS.CLEAR_CURRENT_SELECTED_MEETING:
      return {
        ...state,
        currentSelectedMeeting: null
      };
    
    case ACTIONS.SET_SEARCH_RESULTS:
      return { ...state, searchResults: action.payload };
    
    case ACTIONS.SET_SEARCHING:
      return { ...state, isSearching: action.payload };
    
    case ACTIONS.CLEAR_SEARCH:
      return { ...state, searchResults: null };
    
    case ACTIONS.SET_SORT_PARAMS:
      const { sortBy, sortDescending } = action.payload;
      return { ...state, sortBy, sortDescending };
    
    case ACTIONS.UPDATE_MEETINGS_AND_SELECTED:
      const { meetings, selectedMeeting } = action.payload;
      
      // Check if meetings have actually changed
      const meetingsChanged = !areMeetingsEqual(state.meetings, meetings);
      const selectedMeetingChanged = state.currentSelectedMeeting !== selectedMeeting;
      
      if (!meetingsChanged && !selectedMeetingChanged) {
        return state;
      }
      
      return { 
        ...state, 
        meetings: meetingsChanged ? meetings : state.meetings, // Keep same reference if no change
        currentSelectedMeeting: selectedMeeting 
      };
    
    default:
      return state;
  }
};

// Utility function for sorting meetings
const sortMeetings = (meetings, sortBy, sortDescending) => {
  const sorted = [...meetings].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'title':
        aValue = (a[titleKey] || '').toLowerCase();
        bValue = (b[titleKey] || '').toLowerCase();
        break;
      case 'duration':
        // Use the duration field directly from the server response (in seconds)
        aValue = parseFloat(a[durationKey]) || 0;
        bValue = parseFloat(b[durationKey]) || 0;
        break;
      case 'date':
      case 'id':
      default:
        aValue = a[idKey] || 0;
        bValue = b[idKey] || 0;
        break;
    }
    
    if (aValue < bValue) return sortDescending ? 1 : -1;
    if (aValue > bValue) return sortDescending ? -1 : 1;
    return 0;
  });
  
  return sorted;
};

// Create context
const MeetingContext = createContext();

// Provider component
export const MeetingProvider = ({ children }) => {
  const [state, dispatch] = useReducer(meetingReducer, initialState);
  const pollingIntervalRef = useRef(null);
  const isPollingRef = useRef(false);
  const stateRef = useRef(state);
  
  // Keep state ref updated
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Check if there are any meetings being processed
  const hasProcessingMeetings = useCallback((meetings) => {
    // Filter meetings that are being processed (isSummarized === 0)
    const processingMeetings = meetings.filter(meeting => meeting[isSummarizedKey] === 0);
    return processingMeetings.length > 0;
  }, []);

  // Stop polling for meeting updates
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    isPollingRef.current = false;
  }, []);

  // Start polling for meeting updates
  const startPolling = useCallback(() => {
    if (isPollingRef.current || pollingIntervalRef.current) {
      return; // Already polling
    }
    isPollingRef.current = true;
    pollingIntervalRef.current = setInterval(async () => {
      try {
        // Get current state at polling time to avoid stale closures
        const currentState = stateRef.current;
        const sortBy = currentState.sortBy === 'id' ? 'id' : currentState.sortBy;
        const sortDesc = currentState.sortDescending ? 1 : 0;
        
        // Fetch updated meetings list
        const updatedMeetings = await listMeeting(sortBy, sortDesc);
        
        // Check if meetings have actually changed before doing anything
        if (areMeetingsEqual(currentState.meetings, updatedMeetings)) {
          // Still check if we need to stop polling
          const stillProcessing = hasProcessingMeetings(updatedMeetings);
          if (!stillProcessing) {
            stopPolling();
          }
          return; // Skip the rest if no changes
        }
        
        // Check if the currently selected meeting needs to be reloaded
        let updatedSelectedMeeting = null;
        if (currentState.currentSelectedMeeting) {
          const selectedMeetingId = currentState.currentSelectedMeeting[idKey];
          const updatedSelectedMeetingFromList = updatedMeetings.find(m => m[idKey] === selectedMeetingId);
          
          // Only reload if meeting is processing OR just finished processing
          if (updatedSelectedMeetingFromList && updatedSelectedMeetingFromList[isSummarizedKey] === 0) {
            try {
              updatedSelectedMeeting = await getMeeting(selectedMeetingId);
            } catch (error) {
              console.error('[MeetingContext] Error reloading selected meeting:', error);
              updatedSelectedMeeting = currentState.currentSelectedMeeting; // Keep current if error
            }
          } else if (updatedSelectedMeetingFromList && updatedSelectedMeetingFromList[isSummarizedKey] === 1 && currentState.currentSelectedMeeting[isSummarizedKey] === 0) {
            // Meeting just finished processing - do a final reload
            try {
              updatedSelectedMeeting = await getMeeting(selectedMeetingId);
            } catch (error) {
              console.error('[MeetingContext] Error reloading completed meeting:', error);
              updatedSelectedMeeting = currentState.currentSelectedMeeting; // Keep current if error
            }
          } else {
            // No reload needed, keep current selected meeting
            updatedSelectedMeeting = currentState.currentSelectedMeeting;
          }
        }
        
        // Only dispatch if there are actual changes
        dispatch({ 
          type: ACTIONS.UPDATE_MEETINGS_AND_SELECTED, 
          payload: { 
            meetings: updatedMeetings, 
            selectedMeeting: updatedSelectedMeeting 
          } 
        });
        
        // Check if we still have processing meetings
        const stillProcessing = hasProcessingMeetings(updatedMeetings);
        
        if (!stillProcessing) {
          stopPolling();
        }
      } catch (error) {
        console.error('[MeetingContext] Error during polling:', error);
        // Continue polling even if there's an error
      }
    }, 1000); // Poll every 1 second
  }, [hasProcessingMeetings, stopPolling]);

  // Effect to manage polling based on processing meetings
  useEffect(() => {
    const needsPolling = hasProcessingMeetings(state.meetings);
    
    if (needsPolling) {
      startPolling();
    } else {
      stopPolling();
    }

    // Cleanup on unmount
    return () => {
      stopPolling();
    };
  }, [state.meetings, hasProcessingMeetings, startPolling, stopPolling]);

  // Load meetings list (title + summary only)
  const loadMeetings = useCallback(async (orderBy = 'id', desc = 1, isInitialLoad = false) => {
    
    try {
      if (isInitialLoad) {
        dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      } else {
        dispatch({ type: ACTIONS.SET_SORTING_LOADING, payload: true });
      }
      
      const promises = [listMeeting(orderBy, desc)];
      if (!isInitialLoad) {
        promises.push(new Promise(resolve => setTimeout(resolve, 300)));
      }
      
      const [meetingList] = await Promise.all(promises);
      
      dispatch({ type: ACTIONS.SET_MEETINGS, payload: meetingList });
      dispatch({ type: ACTIONS.SET_ERROR, payload: null });
    } catch (err) {
      console.error('[MeetingContext] loadMeetings error:', err);
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
    } finally {
      if (isInitialLoad) {
        dispatch({ type: ACTIONS.SET_LOADING, payload: false });
      } else {
        dispatch({ type: ACTIONS.SET_SORTING_LOADING, payload: false });
      }
    }
  }, []);

  // Load and set the currently selected meeting with full details
  const selectMeeting = useCallback(async (meetingId) => {
    try {

      // If it's the same meeting, still return it but ensure state consistency
      if (state.currentSelectedMeeting && state.currentSelectedMeeting[idKey] === meetingId) {
        // Ensure the state is properly set (dispatch to trigger re-renders)
        dispatch({ type: ACTIONS.SET_CURRENT_SELECTED_MEETING, payload: state.currentSelectedMeeting });
        return state.currentSelectedMeeting;
      }

      // Fetch full meeting data from server
      const fullMeeting = await getMeeting(meetingId);
      
      if (fullMeeting) {
        dispatch({ type: ACTIONS.SET_CURRENT_SELECTED_MEETING, payload: fullMeeting });
        return fullMeeting;
      }
        
        return null;
    } catch (error) {
      console.error(`Error loading meeting ${meetingId}:`, error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      return null;
    }
  }, [state.currentSelectedMeeting]);

  // Clear the currently selected meeting
  const clearSelectedMeeting = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_CURRENT_SELECTED_MEETING });
  }, []);

  // Get meeting data - returns current selected meeting if it matches, otherwise returns list data
  const getMeetingData = useCallback((meetingId) => {
    // If it's the currently selected meeting, return the full data
    if (state.currentSelectedMeeting && state.currentSelectedMeeting[idKey] === meetingId) {
      return state.currentSelectedMeeting;
    }
    
    // Fallback to list data (title + summary only)
    const listMeeting = state.meetings.find(meeting => meeting[idKey] === meetingId);
    return listMeeting || null;
  }, [state.meetings, state.currentSelectedMeeting]);

  // Check if meeting has full data loaded
  const hasFullMeetingData = useCallback((meetingId) => {
    return state.currentSelectedMeeting && state.currentSelectedMeeting[idKey] === meetingId;
  }, [state.currentSelectedMeeting]);

  // Delete meeting
  const deleteMeeting = useCallback(async (meetingId) => {
    try {
      await delete_meeting(meetingId);
      dispatch({ type: ACTIONS.DELETE_MEETING, payload: meetingId });
    } catch (err) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
    }
  }, []);

  // Toggle pin status
  const togglePin = useCallback(async (meetingId, currentPinStatus) => {
    try {
      await like(meetingId, !currentPinStatus);
      const updates = { [pinedKey]: !currentPinStatus };
      dispatch({ type: ACTIONS.UPDATE_MEETING, payload: { meetingId, updates } });
    } catch (err) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
    }
  }, []);

  // Update meeting
  const updateMeeting = useCallback((meetingId, updates) => {
    dispatch({ type: ACTIONS.UPDATE_MEETING, payload: { meetingId, updates } });
  }, []);

  // Refresh the currently selected meeting from the backend
  const refreshSelectedMeeting = useCallback(async () => {
    if (!state.currentSelectedMeeting) {
      return null;
    }
    
    const meetingId = state.currentSelectedMeeting[idKey];
    try {
      const refreshedMeeting = await getMeeting(meetingId);
      if (refreshedMeeting) {
        dispatch({ type: ACTIONS.SET_CURRENT_SELECTED_MEETING, payload: refreshedMeeting });
        return refreshedMeeting;
      }
      return null;
    } catch (error) {
      console.error(`Error refreshing meeting ${meetingId}:`, error);
      return null;
    }
  }, [state.currentSelectedMeeting]);

  // Search meetings
  const searchMeetingsAction = useCallback(async (searchParams) => {
    try {
      dispatch({ type: ACTIONS.SET_SEARCHING, payload: true });
      const [results] = await Promise.all([
        searchMeetings(
          searchParams.keywords,
          searchParams.fields,
          searchParams.orderBy,
          searchParams.desc
        ),
        new Promise(resolve => setTimeout(resolve, 300))
      ]);
      dispatch({ type: ACTIONS.SET_SEARCH_RESULTS, payload: results });
      return results;
    } catch (error) {
      console.error('Search error:', error);
      dispatch({ type: ACTIONS.SET_SEARCH_RESULTS, payload: [] });
      return [];
    } finally {
      dispatch({ type: ACTIONS.SET_SEARCHING, payload: false });
    }
  }, []);

  // Clear search
  const clearSearch = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_SEARCH });
  }, []);

  // Set sorting parameters
  const setSortParams = useCallback((sortBy, sortDescending) => {
    dispatch({ type: ACTIONS.SET_SORT_PARAMS, payload: { sortBy, sortDescending } });
  }, []);

  // Get display meetings (search results or sorted regular meetings)
  const getDisplayMeetings = useCallback(() => {
    const baseMeetings = state.searchResults !== null ? state.searchResults : state.meetings;
    
    // Always apply client-side sorting to both search results and regular meetings
    return sortMeetings(baseMeetings, state.sortBy, state.sortDescending);
  }, [state.searchResults, state.meetings, state.sortBy, state.sortDescending]);

  const value = {
    // State
    meetings: state.meetings,
    currentSelectedMeeting: state.currentSelectedMeeting,
    loading: state.loading,
    sortingLoading: state.sortingLoading,
    error: state.error,
    searchResults: state.searchResults,
    isSearching: state.isSearching,
    isSearchActive: state.searchResults !== null,
    sortBy: state.sortBy,
    sortDescending: state.sortDescending,
    isPolling: isPollingRef.current,
    
    // Actions
    loadMeetings,
    selectMeeting,
    clearSelectedMeeting,
    refreshSelectedMeeting,
    getMeetingData,
    hasFullMeetingData,
    deleteMeeting,
    togglePin,
    updateMeeting,
    searchMeetings: searchMeetingsAction,
    clearSearch,
    getDisplayMeetings,
    setSortParams,
    hasProcessingMeetings,
    startPolling,
    stopPolling
  };

  return (
    <MeetingContext.Provider value={value}>
      {children}
    </MeetingContext.Provider>
  );
};

// Custom hook to use the meeting context
export const useMeetingContext = () => {
  const context = useContext(MeetingContext);
  if (!context) {
    throw new Error('useMeetingContext must be used within a MeetingProvider');
  }
  return context;
};